import { defineMessages } from 'react-intl';

/**
 * Copy for what happens to an offline transcribe session when its microphone is
 * unplugged mid-consult.
 *
 * Nothing is said at the moment the device drops. Rust reacts to that same loss
 * by reopening the capture on another microphone, and only it knows whether
 * that worked — so these are raised from the outcome events
 * (`offline_recording:input_recovered` / `:input_recovery_failed`) in
 * `useOfflineRecordingEvents`, not from the `ERROR` listener in `ipc.ts`.
 *
 * The online path has always worked this way: `audio-device-lost-recovery.tsx`
 * auto-switches and then says "continues on {device}" or "stopped", never
 * guessing before the outcome is known. This is offline reaching parity.
 *
 * The one place the two differ is the failure copy. Online *stops* the
 * recording when no microphone remains; offline deliberately leaves the capture
 * alive padding silence, so the clinician can end it and keep everything
 * captured up to the unplug. The copy has to reflect that, not online's.
 *
 * Lives here, rather than beside the hook, only because these message ids are
 * already translated — moving the file would change the extracted keys in
 * `src/locales/` and invalidate that work for no user-visible gain.
 */
export const offlineDeviceLostMessages = defineMessages({
  title: {
    defaultMessage: 'Microphone disconnected',
    id: 'qciQM9aLO+',
    description:
      'Destructive toast title when the microphone is unplugged during an offline transcribe session',
  },
  description: {
    defaultMessage:
      'This session is no longer capturing audio. End transcribing to save what was captured.',
    id: 'zEqO9g8Gcw',
    description:
      'Destructive toast body when the microphone is unplugged during an offline transcribe session. The capture stays live padding silence, so ending the session is the only remedy offered.',
  },
  recoveredTitle: {
    defaultMessage: '{device} disconnected',
    id: '0y+nmIgv7q',
    description:
      'Toast title when the microphone is unplugged mid-session and transcribing continues on another one; {device} is the microphone that dropped',
  },
  recoveredDescription: {
    defaultMessage: 'This session is still transcribing, now using {device}.',
    id: 'szk7KXfutc',
    description:
      'Toast body reassuring the clinician the session continued; {device} is the replacement microphone. Worth surfacing rather than hiding, because a built-in microphone may transcribe worse than the headset that dropped.',
  },
  errorDescription: {
    defaultMessage:
      'A microphone error interrupted this session. End transcribing to save what was captured.',
    id: 'xGUEgfr7gG',
    description:
      'Toast body when the microphone is unplugged mid-session and the replacement could not be opened. Distinct from the no-microphone case, which the clinician can act on by plugging one in.',
  },
});

/**
 * Copy for system-audio (ScreenCaptureKit) capture-start failures raised from
 * `listenToEvent()` in `ipc.ts` (DSK-740). Lives here rather than
 * `src/locales/en/common.json` for the same reason as
 * {@link offlineDeviceLostMessages}: that catalog is deprecated for new
 * strings, and this listener has no React context to call `useIntl()` from.
 */
export const sckCaptureFailureMessages = defineMessages({
  permissionDeniedTitle: {
    defaultMessage: 'Error',
    id: 'ihwQ2wOOun',
    description:
      'Destructive toast title when macOS declines Screen Recording (TCC) permission for system-audio capture (SCStreamErrorDomain -3801)',
  },
  permissionDenied: {
    defaultMessage:
      'Screen Recording permission was denied by macOS. Please go to System Settings > Privacy & Security > Screen Recording, remove Heidi, re-add it, and restart the app.',
    id: '+7yQmNm2tB',
    description:
      'Destructive toast body when macOS declines Screen Recording (TCC) permission for system-audio capture (SCStreamErrorDomain -3801), telling the user how to re-grant it',
  },
});
